'use client'

import {useState, useEffect} from 'react'
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  PopoverGroup,
} from '@headlessui/react'
import {
  Bars3Icon,
  XMarkIcon,
  PhoneIcon,
  EnvelopeIcon,
  UserCircleIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import {Calculator, LayoutGrid, Warehouse, Zap} from 'lucide-react';
import Link from 'next/link';
import BtnAccount from "@/components/Layout/Header/BtnAccount";
import BtnCart from "@/components/Layout/Header/BtnCart";
import BtnFavorites from "@/components/Layout/Header/BtnFavorites";
import SearchLineModal from "@/components/Search/SearchLineModal";
import Region from "@/components/Layout/Header/RegionOption";
import CatalogModal from "@/components/Layout/Header/CatalogModal";
import MobileRegionBar from "@/components/Layout/Header/MobileRegionBar";
import {useT} from '@/lib/config/useTranslations'
import {siteConfig} from '@/lib/config/site-config'
import {useMenu} from '@/hooks/useMenu'
import {useLanguage} from '@/lib/config/useLanguage';
import {usePathname, useSearchParams} from 'next/navigation';
import {getBranchCodeFromPath, regionalizePath} from '@/lib/config/branches-config';
import { getLeadFormBranch } from '@/lib/forms/region';
import { isGlobalContentPath, normalizeGlobalContentPath } from '@/lib/config/global-content';

export default function Example({catalogData}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [catalogOpen, setCatalogOpen] = useState(false)
  const {t} = useT()
  const {menuData, loading, error} = useMenu()
  const { localizePath } = useLanguage()
  const pathname = usePathname() || '/'
  const searchParams = useSearchParams()
  const currentBranchCode = getLeadFormBranch(pathname, searchParams.toString())

  // Функция для комбинирования языковой и региональной локализации
  const localizeAndRegionalizePath = (path) => {
    const canonicalPath = normalizeGlobalContentPath(path)
    const localizedPath = localizePath(canonicalPath)
    if (isGlobalContentPath(canonicalPath)) {
      return localizedPath
    }
    return regionalizePath(localizedPath, currentBranchCode)
  }

  const constructionCalculationHref = `${localizePath('/raschet-konstruktsii/')}?branch=${currentBranchCode}`
  const calculatorHref = `${localizePath('/raschet-zdaniya/')}?branch=${currentBranchCode}`
  const accountHref = localizeAndRegionalizePath('/profile/')

  // Формируем структуру меню из API данных
  const getMenuStructure = () => {
    // Проверяем наличие данных
    if (!menuData || !menuData.data || !menuData.data.categories) {
      // Возвращаем пустую структуру, если данных нет
      return [{
        id: 'catalog',
        name: 'Каталог',
        sections: []
      }]
    }

    const apiCategories = menuData.data.categories
    const sections = []

    // Используем все категории первого уровня (с детьми и без)
    const categoriesAll = apiCategories

    // Если нет категорий, возвращаем пустую структуру
    if (!categoriesAll || categoriesAll.length === 0) {
      return [{
        id: 'catalog',
        name: 'Каталог',
        sections: []
      }]
    }

    // Группируем категории по колонкам (максимум 5 колонок)
    const maxColumns = 5
    const itemsPerColumn = Math.ceil(categoriesAll.length / maxColumns)

    for (let i = 0; i < maxColumns; i++) {
      const columnItems = []
      const startIndex = i * itemsPerColumn
      const endIndex = Math.min(startIndex + itemsPerColumn, categoriesAll.length)

      for (let j = startIndex; j < endIndex; j++) {
        const category = categoriesAll[j]
        if (!category) continue // Пропускаем невалидные категории
        
        columnItems.push({
          id: category.id,
          name: category.name,
          href: localizeAndRegionalizePath(category.href || '/shop'), // Локализуем и регионизируем ссылку на категорию
          items: (category.children && category.children.length > 0)
            ? category.children.map(child => ({
              name: child.name,
              href: localizeAndRegionalizePath(child.href || '/shop')
            }))
            : []
        })
      }

      if (columnItems.length > 0) {
        sections.push(columnItems)
      }
    }

    const result = [{
      id: 'catalog',
      name: 'Каталог',
      sections: sections
    }]
    return result
  }

  const dynamicNavigation = {
    categories: getMenuStructure()
  }

  return (
    <>
      <MobileRegionBar />
      <header className="relative isolate z-10 bg-header-gradient">
        <nav aria-label="Global"
             className="w-full max-w-[1536px] mx-auto flex items-center justify-between py-2 px-4 lg:px-8">
          <div className="flex min-w-0 max-w-[134px] flex-1 me-2 sm:max-w-none sm:flex-none">
            <a href={localizeAndRegionalizePath('/')} className="block min-w-0">
              <span className={'text-xl'}></span>
              <img
                alt="shop"
                src={siteConfig.general.logo}
                width={1600}
                height={429}
                className="h-auto w-full max-w-full sm:h-10 sm:w-auto lg:h-12"
              />
            </a>
          </div>
          <div className="flex shrink-0 xl:hidden items-center gap-2">
            <a
              href={calculatorHref}
              aria-label="Калькулятор металлоконструкций"
              title="Калькулятор металлоконструкций"
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              <Calculator aria-hidden="true" className="size-6" />
            </a>
            <a
              href={constructionCalculationHref}
              className="inline-flex min-h-11 max-w-[116px] min-[360px]:max-w-none items-center justify-center gap-1.5 rounded-lg bg-red-600 px-2 py-2 text-center text-xs font-semibold leading-tight text-white shadow-sm transition-colors hover:bg-red-700"
            >
              Расчёт конструкции
            </a>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-site-menu"
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-white"
            >
              <span className="sr-only">Открыть меню</span>
              <Bars3Icon aria-hidden="true" className="size-6"/>
            </button>
          </div>
          <PopoverGroup className="hidden xl:flex items-center xl:flex-1 xl:gap-x-0">
            <button
              onClick={() => setCatalogOpen(true)}
              className="rounded-xl flex flex-row items-center gap-2  px-5 py-3 text-sm font-semibold text-white shadow-xs hover:bg-white/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              <LayoutGrid aria-hidden="true" className="size-5 flex-none text-white"/>
              Каталог
            </button>
            <a href={localizeAndRegionalizePath('/shop/category/bystrovozvodimye-zdaniya-iz-metallokonstruktsiy/')} className="rounded-xl flex flex-row items-center gap-2  px-3 py-3 text-sm font-semibold text-white shadow-xs hover:bg-white/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">
              <Warehouse className="text-white" />
              Быстровозводимые здания</a>

            <a href={localizeAndRegionalizePath('/portfolio/')} className="rounded-xl flex flex-row items-center gap-2  px-3 py-3 text-sm font-semibold text-white shadow-xs hover:bg-white/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">Портфолио</a>
            <a
              href={calculatorHref}
              className="rounded-xl inline-flex items-center gap-2 px-3 py-3 text-sm font-semibold text-white hover:bg-white/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              <Calculator aria-hidden="true" className="size-5 flex-none" />
              Калькулятор
            </a>

            <SearchLineModal/>
            <a
              href={constructionCalculationHref}
              className="ml-3 mr-3 inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-red-700"
            >
              <Zap aria-hidden="true" className="h-4 w-4" />
              Расчёт конструкции
            </a>

          </PopoverGroup>
          <div className="hidden xl:flex items-center xl:justify-end gap-3">
            {/*<Region/>*/}
            <BtnFavorites/>
            <BtnCart/>
            <BtnAccount href={accountHref}/>
          </div>
        </nav>
        <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="xl:hidden">
          <div className="fixed inset-0 z-50 bg-slate-950/40" aria-hidden="true"/>
          <DialogPanel
            id="mobile-site-menu"
            className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white p-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10  px-4 lg:px-8">
            <DialogTitle className="sr-only">Меню сайта</DialogTitle>
            <div className="flex items-center justify-between">
              <a href={localizeAndRegionalizePath('/')} aria-label="На главную" className="inline-flex rounded-lg bg-slate-900 p-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900">
                <img
                  alt="ЗМК Континенталь"
                  src={siteConfig.general.logo}
                  width={1600}
                  height={429}
                  className="h-8 w-auto"
                />
              </a>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="-m-2.5 rounded-md p-2.5 text-gray-700"
              >
                <span className="sr-only">Закрыть меню</span>
                <XMarkIcon aria-hidden="true" className="size-6"/>
              </button>
            </div>
            <Link
              href={accountHref}
              onClick={() => setMobileMenuOpen(false)}
              aria-current={/\/profile(?:\/|$)/.test(pathname) ? 'page' : undefined}
              className="mt-6 flex min-h-16 items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-slate-900 transition-colors hover:bg-slate-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
            >
              <UserCircleIcon aria-hidden="true" className="h-7 w-7 shrink-0 text-slate-600" />
              <span className="min-w-0 flex-1">
                <span className="block text-base font-semibold">Личный кабинет</span>
                <span className="mt-1 block text-xs leading-5 text-slate-600">Контактные данные и мои заказы</span>
              </span>
              <ChevronRightIcon aria-hidden="true" className="h-5 w-5 shrink-0 text-slate-500" />
            </Link>
            <div className="mt-6 pb-6 border-b border-gray-200">
              <a
                href={constructionCalculationHref}
                onClick={() => setMobileMenuOpen(false)}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-5 py-3 text-base font-semibold text-white shadow-sm transition-colors hover:bg-red-700"
              >
                <Zap aria-hidden="true" className="h-5 w-5" />
                Расчёт конструкции
              </a>
            </div>
            <div className="mt-6 flow-root">
              <div className="space-y-2">
                <button
                  onClick={() => {
                    setMobileMenuOpen(false)
                    setCatalogOpen(true)
                  }}
                  className="-mx-3 w-[calc(100%+1.5rem)] flex items-center justify-start gap-2 rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  <LayoutGrid aria-hidden="true" className="size-5 flex-none"/>
                  Каталог
                </button>
                <a
                  href={localizeAndRegionalizePath('/shop/category/bystrovozvodimye-zdaniya-iz-metallokonstruktsiy/')}
                  onClick={() => setMobileMenuOpen(false)}
                  className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  Быстровозводимые здания
                </a>
                <a
                  href={localizeAndRegionalizePath('/portfolio/')}
                  onClick={() => setMobileMenuOpen(false)}
                  className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  Портфолио
                </a>
                <a
                  href={localizeAndRegionalizePath('/sales/')}
                  onClick={() => setMobileMenuOpen(false)}
                  className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  Акции
                </a>
                <a
                  href={localizeAndRegionalizePath('/company/contacts/')}
                  onClick={() => setMobileMenuOpen(false)}
                  className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  Контакты
                </a>
                <a
                  href={localizeAndRegionalizePath('/reviews/')}
                  onClick={() => setMobileMenuOpen(false)}
                  className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  Отзывы
                </a>
                <a
                  href={localizeAndRegionalizePath('/company/faq/')}
                  onClick={() => setMobileMenuOpen(false)}
                  className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  Частые вопросы
                </a>
                <a
                  href={localizeAndRegionalizePath('/pages/dostavka/')}
                  onClick={() => setMobileMenuOpen(false)}
                  className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  Доставка
                </a>
                <a
                  href={localizeAndRegionalizePath('/pages/oplata/')}
                  onClick={() => setMobileMenuOpen(false)}
                  className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  Оплата
                </a>
              </div>

              <div className="mt-6 border-t border-gray-200 pt-6 space-y-2">
                <a
                  href="tel:+79217590752"
                  onClick={() => setMobileMenuOpen(false)}
                  className="-mx-3 flex items-center gap-3 rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  <PhoneIcon className="h-5 w-5 flex-none" />
                  +7 (921) 759-07-52
                </a>
                <a
                  href="mailto:sales@ur-met.ru"
                  onClick={() => setMobileMenuOpen(false)}
                  className="-mx-3 flex items-center gap-3 rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  <EnvelopeIcon className="h-5 w-5 flex-none" />
                  sales@ur-met.ru
                </a>
                <a
                  href="https://t.me/state_street_corporation"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="-mx-3 flex items-center gap-3 rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  <img src="/icon/Telegram.svg" alt="Telegram" className="h-5 w-5 flex-none" />
                  Telegram
                </a>
                <a
                  href="https://max.ru/u/f9LHodD0cOKkTKl7uHBoM9NKG6P5BepSPikVN6knzBtuCqb1-uy75Fth6c8"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="-mx-3 flex items-center gap-3 rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  <img src="/icon/max.svg" alt="MAX" className="h-5 w-5 flex-none" />
                  max
                </a>
              </div>
            </div>
          </DialogPanel>
        </Dialog>
        <CatalogModal open={catalogOpen} onOpenChange={setCatalogOpen} categories={catalogData} />
      </header>
    </>
  )
}
